import { Play } from "lucide-react";
import phlebotomyDraw from "@/assets/phlebotomy-draw.jpg";
import phlebotomyVials from "@/assets/phlebotomy-vials.jpg";
import biometricEvent from "@/assets/biometric-screening-event.jpg";
import bloodDrawHome from "@/assets/blood-draw-home.jpg";
import { useRef, useState } from "react";

const BloodDrawMedia = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
      setPlaying(true);
    } catch {
      video.muted = true;
      try {
        await video.play();
        setPlaying(true);
      } catch {
        // fallback: no video available
      }
    }
  };

  return (
    <section className="py-8 px-4 bg-muted/30">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-6">
          <h2 className="text-xl md:text-3xl font-bold text-primary-dark mb-2">
            Professional Blood Draw Services
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto text-sm hidden md:block">
            Our certified phlebotomists come to you — clean, safe, and done in minutes.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Photo 1 */}
          <div className="relative rounded-xl overflow-hidden shadow-md group aspect-[4/3]">
            <img
              src={phlebotomyDraw}
              alt="Certified phlebotomist performing a blood draw"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-medium">
              Professional Blood Draw
            </p>
          </div>

          {/* Video Card */}
          <div className="relative rounded-xl overflow-hidden shadow-md group aspect-[4/3] bg-primary-dark/90">
            <video
              ref={videoRef}
              playsInline
              muted
              loop
              preload="none"
              poster={biometricEvent}
              className={`w-full h-full object-cover transition-opacity duration-300 ${playing ? "opacity-100" : "opacity-60"}`}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            {!playing && (
              <>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  onClick={handlePlay}
                >
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 border-2 border-white/50 hover:bg-white/30 transition-all">
                    <Play className="h-6 w-6 text-white fill-white" />
                  </div>
                </div>
                <p className="absolute bottom-2 left-2 text-white text-xs font-medium">
                  Watch How It Works
                </p>
              </>
            )}
          </div>

          {/* Photo 2 */}
          <div className="relative rounded-xl overflow-hidden shadow-md group aspect-[4/3]">
            <img
              src={phlebotomyVials}
              alt="Blood collection vials used in testing"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-medium">
              Accurate Lab-Grade Collection
            </p>
          </div>

          {/* Photo 3 */}
          <div className="relative rounded-xl overflow-hidden shadow-md group aspect-[4/3]">
            <img
              src={bloodDrawHome}
              alt="Professional phlebotomist blood draw close-up"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-medium">
              Expert Phlebotomy Care
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          All collections follow strict chain-of-custody protocols by AABB-certified professionals.
        </p>
      </div>
    </section>
  );
};

export default BloodDrawMedia;
